import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Alert, Autocomplete, Button, MenuItem, TextField, Typography } from '@mui/material';
import {
  DEADLINE_DOMAIN_VALUES,
  type DeadlineAiProposal,
  isOtherDocumentType,
  resolveRecurrencePreselection,
} from '@prospero/shared';
import { ConsultantDeadlineFormDto } from '@shared/dto/deadline/consultant-deadline-form.dto';
import type { ConsultantBulkCreateReport } from '@shared/dto/deadline/consultant-deadline-write.response';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AiProposedLabel } from '../../../../components/ai-act/ai-proposed-label';
import {
  asksWarningDays,
  type DeadlineRecurrenceValue,
} from '../../../../components/deadline-recurrence/deadline-recurrence';
import { DeadlineRecurrenceFields } from '../../../../components/deadline-recurrence/deadline-recurrence-fields';
import { prefillValiditySuggestion } from '../../../../components/deadline-recurrence/prefill-validity-suggestion';
import { FormDialog } from '../../../../components/dialogs/form-dialog';
import { DocumentTypeHint } from '../../../../components/document-type-hint';
import { HealthDataNotice } from '../../../../components/health-data-notice';
import { getLocalTodayIso } from '../../../../config/timezone';
import { useCreateConsultantDeadlines } from '../../../../hooks/consultant/use-consultant-deadline-actions';
import {
  buildDeadlineAiFieldProposal,
  type CreateDialogFillPlan,
  planCreateDialogFill,
  planCreateDialogWithdrawal,
} from '../../../../hooks/deadline/deadline-ai-field-proposal';
import {
  type AiFilledFields,
  useAiFilledFields,
} from '../../../../hooks/deadline/use-ai-filled-fields';
import {
  type DeadlineDocumentReading,
  isUploadTokenInvalidError,
  useDeadlineDocumentReading,
} from '../../../../hooks/deadline/use-deadline-document-reading';
import { useValiditySuggestion } from '../../../../hooks/deadline/use-validity-suggestion';
import { useDocumentTypes } from '../../../../hooks/document-type/use-document-types';
import { useIsMobile } from '../../../../hooks/use-is-mobile';
import { useNotification } from '../../../../hooks/use-notification';
import type { DocumentTypeItem } from '../../../../services/document-type/document-type.service';
import { getDeadlineFormDefaults } from '../../../../services/document-type/document-type-defaults';
import {
  ASSISTANT_CARD_BUSY_CODE,
  ASSISTANT_CARD_CONFIRMED_CODE,
} from '../../../../utils/assistant-card-error-codes';
import { getHttpErrorCode } from '../../../../utils/error.utils';
import { DeadlineWarningDaysField } from '../../../documents-archive/deadlines/DeadlineWarningDaysField';
import type { AssistantDraftDialogValues } from '../mappers/assistant-draft-dialog.mapper';
import {
  type ConsultantDeadlineFormValues,
  toBulkCreateDeadlinesDto,
} from '../mappers/consultant-deadline-form.mapper';
import { resolveCompanySignatureAvailability } from './company-signature-availability';
import { type ClientOption, ConsultantClientMultiSelect } from './consultant-client-multi-select';
import { ConsultantDeadlineAssigneeField } from './consultant-deadline-assignee-field';
import { ConsultantDeadlineCreateDocumentSection } from './consultant-deadline-create-document-section';
import { ConsultantDeadlineCreateIntro } from './consultant-deadline-create-intro';
import { CreateSignatureRequestField } from './consultant-deadline-create-signature-field';
import {
  type CreateDeadlineInputs,
  listMissingCreateInputs,
} from './consultant-deadline-missing-inputs';
import { ConsultantDocumentUploaderField } from './consultant-document-uploader-field';
import { resolveDocumentUploaderGate } from './consultant-document-uploader-gate';
import { ConsultantSameTypeNotice } from './consultant-same-type-notice';

const SET_VALUE_OPTIONS = { shouldValidate: true, shouldDirty: true } as const;

const DEFAULT_VALUES: ConsultantDeadlineFormValues = {
  tenantIds: [],
  documentTypeId: '',
  titleRequired: false,
  title: '',
  recurrence: undefined,
  warningDays: [],
  // Di default il documento lo carica il consulente, che lo allega subito.
  clientCanUpload: false,
  requestClientSignature: false,
  assigneeUserId: null,
};

type AiFilledField = 'documentTypeId' | 'recurrence' | 'dueAt' | 'validityMonths';

const RECURRENCE_AI_FIELDS: readonly AiFilledField[] = ['recurrence', 'dueAt', 'validityMonths'];

/**
 * The fields the assistant read from the card document, as the dialog labels them. The due date
 * computed from an issue date the AI read is proposed by the AI too.
 */
function toDialogAiFields(
  initialDraft: AssistantDraftDialogValues,
): Partial<Record<AiFilledField, string | number>> {
  const { aiFields, values } = initialDraft;
  const fields: Partial<Record<AiFilledField, string | number>> = {};
  if (aiFields.includes('documentType') && values.documentTypeId) {
    fields.documentTypeId = values.documentTypeId;
  }
  if ((aiFields.includes('dueAt') || aiFields.includes('issuedAt')) && values.dueAt) {
    fields.dueAt = values.dueAt;
  }
  if (aiFields.includes('validityMonths') && values.validityMonths !== undefined) {
    fields.validityMonths = values.validityMonths;
  }
  return fields;
}

interface CreateFailureActions {
  markExpired: () => void;
  /** Only when opened from an assistant card. */
  card?: { close: () => void; markBusy: () => void };
}

/**
 * A rejected token asks for the file again; a card closed meanwhile closes the dialog, so the card
 * reloads its outcomes instead of inviting a save that can no longer succeed. A card still busy
 * keeps the dialog open with what was typed: the save can be sent again in a few seconds.
 */
function handleCreateFailure(error: unknown, actions: CreateFailureActions): void {
  if (isUploadTokenInvalidError(error)) {
    actions.markExpired();
    return;
  }
  const code = getHttpErrorCode(error);
  if (code === ASSISTANT_CARD_CONFIRMED_CODE) {
    actions.card?.close();
  } else if (code === ASSISTANT_CARD_BUSY_CODE) {
    actions.card?.markBusy();
  }
}

/** The card is still being created by another request: the typed values stay, the save can be sent again. */
function AssistantCardBusyNotice({ visible }: { visible: boolean }) {
  const { t } = useTranslation();
  if (!visible) {
    return null;
  }
  return (
    <Alert severity="warning" data-testid="consultant-deadline-create-card-busy">
      {t('consultant.deadlines.create.assistantCardBusy', {
        defaultValue:
          "Queste scadenze sono in corso di creazione da un'altra richiesta: attendi qualche secondo e salva di nuovo.",
      })}
    </Alert>
  );
}

/** "Crea scadenza" disattivato dice cosa manca, nell'ordine dei campi (come il dialog di rinnovo). */
function CreateMissingHint({
  isSaveAllowed,
  inputs,
}: {
  isSaveAllowed: boolean;
  inputs: CreateDeadlineInputs;
}) {
  const { t } = useTranslation();
  const missing = isSaveAllowed ? [] : listMissingCreateInputs(inputs);
  if (missing.length === 0) {
    return null;
  }
  const items = missing.map((input) => t(`consultant.deadlines.create.missing.${input}`));
  return (
    <Typography
      variant="body2"
      aria-live="polite"
      sx={{ color: 'text.secondary' }}
      data-testid="consultant-deadline-create-missing-hint"
    >
      {t('consultant.deadlines.create.missingHint', { items: items.join(', ') })}
    </Typography>
  );
}

/** Con più clienti il pulsante dice quante scadenze crea: una per cliente. */
function useCreateSubmitLabel(clientCount: number): string {
  const { t } = useTranslation();
  return clientCount > 1
    ? t('consultant.deadlines.create.submitForClients', { count: clientCount })
    : t('consultant.deadlines.create.submit');
}

/** Un cliente saltato o un errore vanno letti nell'esito: non si prosegue con una nuova scadenza. */
function hasOutcomeToReview(report: ConsultantBulkCreateReport): boolean {
  return report.skipped.length > 0 || report.failed.length > 0;
}

/** Il form si svuota al prossimo render: il campo da riempire per primo prende il focus dopo. */
function focusAfterRender(inputRef: RefObject<HTMLInputElement | null>): void {
  setTimeout(() => inputRef.current?.focus(), 0);
}

/** Non per l'assistente (la card si chiude col salvataggio) né per più clienti. */
function isSaveAndAddAnotherAvailable(
  initialDraft: AssistantDraftDialogValues | undefined,
  tenantIds: string[],
): boolean {
  return !initialDraft && tenantIds.length <= 1;
}

interface SaveAndAddAnotherButtonProps {
  isAvailable: boolean;
  isSaveAllowed: boolean;
  isCreating: boolean;
  onClick: () => void;
}

function SaveAndAddAnotherButton({
  isAvailable,
  isSaveAllowed,
  isCreating,
  onClick,
}: SaveAndAddAnotherButtonProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  if (!isAvailable) {
    return null;
  }
  return (
    <Button
      type="button"
      variant="outlined"
      disabled={!isSaveAllowed || isCreating}
      fullWidth={isMobile}
      sx={{ minHeight: 48 }}
      onClick={onClick}
      data-testid="consultant-deadline-create-submit-and-add-another"
    >
      {t('consultant.deadlines.create.submitAndAddAnother', {
        defaultValue: "Salva e aggiungine un'altra",
      })}
    </Button>
  );
}

interface CreateSubmitHandlersOptions {
  reading: DeadlineDocumentReading;
  initialDraft: AssistantDraftDialogValues | undefined;
  clientNames: Record<string, string>;
  onClose: () => void;
  onCreated: (report: ConsultantBulkCreateReport, tenantNames: Record<string, string>) => void;
  /** Dopo "Salva e aggiungine un'altra": il form riparte per la scadenza successiva. */
  onContinue: (values: ConsultantDeadlineFormValues) => void;
  setIsAssistantCardBusy: (isBusy: boolean) => void;
}

/**
 * I due salvataggi del form. Il documento in deposito si attende e si chiama il bulk; se non va a
 * buon fine il dialog resta aperto con i valori inseriti.
 */
function useCreateSubmitHandlers({
  reading,
  initialDraft,
  clientNames,
  onClose,
  onCreated,
  onContinue,
  setIsAssistantCardBusy,
}: CreateSubmitHandlersOptions) {
  const { t: tDocuments } = useTranslation('documents');
  const { showError } = useNotification();
  const createDeadlines = useCreateConsultantDeadlines();

  async function resolveUploadToken(): Promise<string | undefined | null> {
    if (reading.file === null) {
      return undefined;
    }
    const token = await reading.settle();
    if (token === null) {
      showError(tDocuments('deadlines.aiPreview.uploadMissing'));
    }
    return token;
  }

  async function createFromValues(
    values: ConsultantDeadlineFormValues,
  ): Promise<ConsultantBulkCreateReport | null> {
    const uploadToken = await resolveUploadToken();
    if (uploadToken === null) {
      return null;
    }
    setIsAssistantCardBusy(false);
    try {
      return await createDeadlines.mutateAsync({
        ...toBulkCreateDeadlinesDto(values, uploadToken),
        ...(initialDraft && { assistantActionId: initialDraft.assistantActionId }),
      });
    } catch (error) {
      // Il hook ha già mostrato l'errore (la card occupata lo dice solo il dialog, sotto il form).
      handleCreateFailure(error, {
        markExpired: reading.markExpired,
        card: initialDraft && { close: onClose, markBusy: () => setIsAssistantCardBusy(true) },
      });
      return null;
    }
  }

  async function onSubmit(values: ConsultantDeadlineFormValues) {
    const report = await createFromValues(values);
    if (report) {
      onCreated(report, clientNames);
      onClose();
    }
  }

  async function onSubmitAndAddAnother(values: ConsultantDeadlineFormValues) {
    const report = await createFromValues(values);
    if (report && hasOutcomeToReview(report)) {
      onCreated(report, clientNames);
      onClose();
    } else if (report) {
      // Il toast "Scadenze create" lo mostra già il hook: qui basta ripartire sul prossimo inserimento.
      onContinue(values);
    }
  }

  return { onSubmit, onSubmitAndAddAnother, isCreating: createDeadlines.isPending };
}

interface AssistantDraftPrefillOptions
  extends Pick<UseFormReturn<ConsultantDeadlineFormValues>, 'reset' | 'getValues' | 'setValue'> {
  open: boolean;
  initialDraft: AssistantDraftDialogValues | undefined;
  setClientNames: (names: Record<string, string>) => void;
  adoptDocument: DeadlineDocumentReading['adopt'];
  markAiFilled: AiFilledFields<AiFilledField>['markAiFilled'];
  otherDocumentTypeId: string | undefined;
}

/**
 * "Modifica e crea": the card draft fills the form once per opening (later renders must not
 * reset what the consultant typed) and its staged document is adopted. A free name without a type
 * is the catalog "Altro" here, set once the types are loaded. Returns the reset for the closing.
 */
function useAssistantDraftPrefill({
  open,
  initialDraft,
  reset,
  getValues,
  setValue,
  setClientNames,
  adoptDocument,
  markAiFilled,
  otherDocumentTypeId,
}: AssistantDraftPrefillOptions): () => void {
  const appliedDraftRef = useRef<AssistantDraftDialogValues | null>(null);
  useEffect(() => {
    if (!open || !initialDraft || appliedDraftRef.current === initialDraft) {
      return;
    }
    appliedDraftRef.current = initialDraft;
    reset({ ...DEFAULT_VALUES, ...initialDraft.values });
    setClientNames(
      Object.fromEntries(
        initialDraft.clients.map((client) => [client.tenantId, client.tenantName]),
      ),
    );
    if (initialDraft.document) {
      adoptDocument(initialDraft.document.uploadToken, initialDraft.document.filename, {
        aiRead: initialDraft.aiFields.length > 0,
      });
      markAiFilled(toDialogAiFields(initialDraft));
    }
  }, [open, initialDraft, reset, setClientNames, adoptDocument, markAiFilled]);

  const needsOtherType =
    open &&
    initialDraft?.values.titleRequired === true &&
    initialDraft.values.documentTypeId === '';
  useEffect(() => {
    if (needsOtherType && otherDocumentTypeId && getValues('documentTypeId') === '') {
      setValue('documentTypeId', otherDocumentTypeId, SET_VALUE_OPTIONS);
    }
  }, [needsOtherType, otherDocumentTypeId, getValues, setValue]);

  return () => {
    appliedDraftRef.current = null;
  };
}

interface ConsultantDeadlineCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (report: ConsultantBulkCreateReport, tenantNames: Record<string, string>) => void;
  /** Opened from a client group: that client comes preselected, others can still be added. */
  initialClient?: ClientOption;
  /**
   * Opened with "Modifica e crea" from an assistant card: the form starts from its draft, the
   * staged document is adopted (no new reading, no quota) and the save closes that card.
   */
  initialDraft?: AssistantDraftDialogValues;
}

export function ConsultantDeadlineCreateDialog({
  open,
  onClose,
  onCreated,
  initialClient,
  initialDraft,
}: ConsultantDeadlineCreateDialogProps) {
  const { t } = useTranslation();
  const typeInputRef = useRef<HTMLInputElement>(null);
  const aiFilled = useAiFilledFields<AiFilledField>();
  const { documentTypes } = useDocumentTypes();
  const todayIso = getLocalTodayIso();
  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [signatureAvailabilityByTenant, setSignatureAvailabilityByTenant] = useState<
    Record<string, boolean>
  >({});
  const [hasChosenUploader, setHasChosenUploader] = useState(false);
  const [isAssistantCardBusy, setIsAssistantCardBusy] = useState(false);
  // Cambia a ogni scadenza salvata con "aggiungine un'altra": la sezione documento riparte pulita.
  const [documentSectionKey, setDocumentSectionKey] = useState(0);

  const { control, register, handleSubmit, reset, setValue, getValues, watch, formState } =
    useForm<ConsultantDeadlineFormValues>({
      resolver: classValidatorResolver(ConsultantDeadlineFormDto),
      defaultValues: DEFAULT_VALUES,
      mode: 'onChange',
    });

  const initialTenantId = initialClient?.tenantId;
  const initialTenantName = initialClient?.tenantName;
  const initialSignatureAvailability = initialClient?.canRequestCompanySignature;
  useEffect(() => {
    if (open && initialTenantId && initialTenantName !== undefined) {
      reset({ ...DEFAULT_VALUES, tenantIds: [initialTenantId] });
      setClientNames({ [initialTenantId]: initialTenantName });
      setSignatureAvailabilityByTenant(
        initialSignatureAvailability === undefined
          ? {}
          : { [initialTenantId]: initialSignatureAvailability },
      );
    }
  }, [open, initialTenantId, initialTenantName, initialSignatureAvailability, reset]);

  const titleRequired = watch('titleRequired');
  const recurrence = watch('recurrence');
  const dueAt = watch('dueAt');
  const validityMonths = watch('validityMonths');
  const selectedTenantIds = watch('tenantIds');
  const documentTypeId = watch('documentTypeId');
  const assigneeUserId = watch('assigneeUserId');
  const sortedDocumentTypes = useMemo(
    () =>
      [...documentTypes].sort(
        (first, second) =>
          first.sortOrder - second.sortOrder || first.name.localeCompare(second.name),
      ),
    [documentTypes],
  );
  const selectedType = sortedDocumentTypes.find((type) => type.id === documentTypeId) ?? null;
  const showTitleError =
    Boolean(formState.errors.title) &&
    (formState.touchedFields.title === true || formState.isSubmitted);
  const {
    suggestion,
    isLoading: isSuggestionLoading,
    fetchSuggestion,
  } = useValiditySuggestion('consultant', selectedType?.id ?? null);

  const singleTenantId = selectedTenantIds.length === 1 ? (selectedTenantIds[0] ?? '') : '';
  const reading = useDeadlineDocumentReading({
    scope: { kind: 'consultant', tenantId: singleTenantId },
    onProposal: (proposal) => handleProposal(proposal),
    onProposalWithdrawn: () => withdrawAiProposal(),
    // La quota è quella del cliente scelto: con il cliente cambia anche la domanda.
    quotaEnabled: open && singleTenantId !== '',
  });

  const forgetAssistantDraft = useAssistantDraftPrefill({
    open,
    initialDraft,
    reset,
    getValues,
    setValue,
    setClientNames,
    adoptDocument: reading.adopt,
    markAiFilled: aiFilled.markAiFilled,
    otherDocumentTypeId: sortedDocumentTypes.find(isOtherDocumentType)?.id,
  });

  /** Art. 50: senza la lettura che li ha proposti, i valori dell'IA non toccati non restano. */
  function withdrawAiProposal() {
    const plan = planCreateDialogWithdrawal(
      aiFilled.withdraw({
        documentTypeId: getValues('documentTypeId'),
        recurrence: getValues('recurrence'),
        dueAt: getValues('dueAt'),
        validityMonths: getValues('validityMonths'),
      }),
      { type: aiFilled.isTouched('documentTypeId'), recurrence: aiFilled.isTouched('recurrence') },
    );
    if (plan.type === 'RESET') {
      void applyDocumentType(null);
    } else if (plan.type === 'CLEAR_ONLY') {
      setValue('documentTypeId', '', SET_VALUE_OPTIONS);
      setValue('titleRequired', false, SET_VALUE_OPTIONS);
    }
    for (const field of plan.emptiedFields) {
      setValue(field, undefined, SET_VALUE_OPTIONS);
    }
  }
  const { onSubmit, onSubmitAndAddAnother, isCreating } = useCreateSubmitHandlers({
    reading,
    initialDraft,
    clientNames,
    onClose,
    onCreated,
    onContinue: restartForNextDeadline,
    setIsAssistantCardBusy,
  });
  const clientCanUpload = watch('clientCanUpload');
  const uploaderGate = resolveDocumentUploaderGate({
    clientCanUpload,
    clientCount: selectedTenantIds.length,
    hasDocument: reading.file !== null,
  });

  async function handleProposal(proposal: DeadlineAiProposal) {
    const chosenTypeId = getValues('documentTypeId') || null;
    const plan = planCreateDialogFill(
      buildDeadlineAiFieldProposal({
        proposal,
        documentTypes: sortedDocumentTypes,
        chosenTypeId,
        todayIso,
      }),
      { type: aiFilled.isTouched('documentTypeId'), recurrence: aiFilled.isTouched('recurrence') },
      chosenTypeId !== null,
    );
    const aiType = plan.documentTypeId
      ? sortedDocumentTypes.find((type) => type.id === plan.documentTypeId)
      : undefined;
    if (aiType) {
      await applyDocumentType(aiType);
    }
    aiFilled.markAiFilled({
      ...(aiType && { documentTypeId: aiType.id }),
      ...applyRecurrencePlan(plan),
    });
  }

  /** Ritorna solo i campi che l'IA ha davvero cambiato: un valore già uguale non è "proposto". */
  function applyRecurrencePlan(
    plan: CreateDialogFillPlan,
  ): Partial<Record<AiFilledField, string | number>> {
    const changed: Partial<Record<AiFilledField, string | number>> = {};
    if (plan.recurrence && plan.recurrence !== getValues('recurrence')) {
      setValue('recurrence', plan.recurrence, SET_VALUE_OPTIONS);
      changed.recurrence = plan.recurrence;
    }
    if (plan.dueAt && plan.dueAt !== getValues('dueAt')) {
      setValue('dueAt', plan.dueAt, SET_VALUE_OPTIONS);
      changed.dueAt = plan.dueAt;
    }
    if (plan.validityMonths !== undefined && plan.validityMonths !== getValues('validityMonths')) {
      setValue('validityMonths', plan.validityMonths, SET_VALUE_OPTIONS);
      changed.validityMonths = plan.validityMonths;
    }
    return changed;
  }

  function handleTypeChange(documentType: DocumentTypeItem | null) {
    aiFilled.touch('documentTypeId');
    return applyDocumentType(documentType);
  }

  async function applyDocumentType(documentType: DocumentTypeItem | null) {
    const defaults = getDeadlineFormDefaults(documentType);
    setValue('documentTypeId', documentType?.id ?? '', SET_VALUE_OPTIONS);
    if (!documentType?.isPersonal) {
      setValue('assigneeUserId', null, SET_VALUE_OPTIONS);
    }
    setValue(
      'titleRequired',
      documentType !== null && isOtherDocumentType(documentType),
      SET_VALUE_OPTIONS,
    );
    setValue('warningDays', defaults.warningDays, SET_VALUE_OPTIONS);
    setValue('domain', defaults.domain ?? undefined, SET_VALUE_OPTIONS);
    const preselected = resolveRecurrencePreselection(documentType);
    setValue('recurrence', preselected, SET_VALUE_OPTIONS);
    setValue('validityMonths', undefined, SET_VALUE_OPTIONS);
    if (preselected === 'NONE') {
      setValue('assigneeUserId', null, SET_VALUE_OPTIONS);
    }
    if (preselected === 'RECURRING' && documentType) {
      await prefillValiditySuggestion({
        documentTypeId: documentType.id,
        fetchSuggestion,
        canApply: () =>
          getValues('documentTypeId') === documentType.id &&
          getValues('validityMonths') === undefined,
        applyMonths: (months) => setValue('validityMonths', months, SET_VALUE_OPTIONS),
      });
    }
  }

  function handleRecurrenceChange(next: DeadlineRecurrenceValue) {
    aiFilled.touch('recurrence');
    setValue('recurrence', next.recurrence, SET_VALUE_OPTIONS);
    setValue('dueAt', next.dueAt, SET_VALUE_OPTIONS);
    setValue('validityMonths', next.validityMonths, SET_VALUE_OPTIONS);
    if (next.recurrence === 'NONE') {
      setValue('assigneeUserId', null, SET_VALUE_OPTIONS);
    }
  }

  function handleClientsChange(
    chosenClients: ClientOption[],
    onFieldChange: (ids: string[]) => void,
  ) {
    setClientNames((current) => ({
      ...current,
      ...Object.fromEntries(chosenClients.map((client) => [client.tenantId, client.tenantName])),
    }));
    const nextAvailability = {
      ...signatureAvailabilityByTenant,
      ...Object.fromEntries(
        chosenClients.flatMap((client) =>
          client.canRequestCompanySignature === undefined
            ? []
            : [[client.tenantId, client.canRequestCompanySignature]],
        ),
      ),
    };
    setSignatureAvailabilityByTenant(nextAvailability);
    const chosenTenantIds = chosenClients.map((client) => client.tenantId);
    // Nessuno dei clienti firma dall'app: la richiesta non resta accesa di nascosto.
    if (resolveCompanySignatureAvailability(chosenTenantIds, nextAvailability) === 'NONE') {
      setValue('requestClientSignature', false, SET_VALUE_OPTIONS);
    }
    onFieldChange(chosenTenantIds);
    // Il token è legato al cliente per cui il file è stato letto.
    reading.discard();
    setValue('assigneeUserId', null, SET_VALUE_OPTIONS);
  }

  /** Verso "Lo carico io" il cliente è uno solo: resta il primo scelto, gli altri si scartano. */
  function handleClientCanUploadChange(next: boolean) {
    setHasChosenUploader(true);
    setValue('clientCanUpload', next, SET_VALUE_OPTIONS);
    const chosenTenantIds = getValues('tenantIds');
    if (next || chosenTenantIds.length <= 1) {
      return;
    }
    setValue('tenantIds', chosenTenantIds.slice(0, 1), SET_VALUE_OPTIONS);
    reading.discard();
    setValue('assigneeUserId', null, SET_VALUE_OPTIONS);
  }

  /** Il cliente, chi carica e la firma restano: cambia solo la scadenza da inserire. */
  function restartForNextDeadline(values: ConsultantDeadlineFormValues) {
    reset({
      ...DEFAULT_VALUES,
      tenantIds: values.tenantIds,
      clientCanUpload: values.clientCanUpload,
      requestClientSignature: values.requestClientSignature,
    });
    setHasChosenUploader(false);
    setDocumentSectionKey((current) => current + 1);
    reading.discard();
    aiFilled.reset();
    focusAfterRender(typeInputRef);
  }

  const isSaveAllowed = formState.isValid && uploaderGate === 'ready';
  const submitLabel = useCreateSubmitLabel(selectedTenantIds.length);
  const createInputs: CreateDeadlineInputs = {
    clientCount: selectedTenantIds.length,
    uploaderGate,
    documentTypeId,
    titleRequired,
    title: watch('title'),
    recurrence,
    dueAt,
    validityMonths,
  };
  const saveAndAddAnotherButton = (
    <SaveAndAddAnotherButton
      isAvailable={isSaveAndAddAnotherAvailable(initialDraft, selectedTenantIds)}
      isSaveAllowed={isSaveAllowed}
      isCreating={isCreating}
      onClick={() => void handleSubmit(onSubmitAndAddAnother)()}
    />
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('consultant.deadlines.create.title')}
      submitLabel={submitLabel}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isCreating}
      isValid={isSaveAllowed}
      slots={{ secondaryAction: saveAndAddAnotherButton }}
      onTransitionExited={() => {
        reset(DEFAULT_VALUES);
        setClientNames({});
        setSignatureAvailabilityByTenant({});
        setHasChosenUploader(false);
        setIsAssistantCardBusy(false);
        reading.discard();
        aiFilled.reset();
        forgetAssistantDraft();
      }}
    >
      <ConsultantDeadlineCreateIntro />

      <ConsultantDocumentUploaderField
        clientCanUpload={clientCanUpload}
        onChange={handleClientCanUploadChange}
        isCreation
        testIdPrefix="consultant-deadline-create-uploader"
      />

      <Controller
        name="tenantIds"
        control={control}
        render={({ field, fieldState }) => (
          <ConsultantClientMultiSelect
            single={!clientCanUpload}
            selected={field.value.map((tenantId) => ({
              tenantId,
              tenantName: clientNames[tenantId] ?? tenantId,
              canRequestCompanySignature: signatureAvailabilityByTenant[tenantId],
            }))}
            onChange={(chosenClients) => handleClientsChange(chosenClients, field.onChange)}
            error={!!fieldState.error}
          />
        )}
      />

      <ConsultantDeadlineCreateDocumentSection
        key={documentSectionKey}
        reading={reading}
        clientCanUpload={clientCanUpload}
        gate={uploaderGate}
        clientCount={selectedTenantIds.length}
        isSubmitted={formState.isSubmitted || hasChosenUploader}
      />

      <CreateSignatureRequestField
        control={control}
        availability={resolveCompanySignatureAvailability(
          selectedTenantIds,
          signatureAvailabilityByTenant,
        )}
      />

      <Autocomplete
        options={sortedDocumentTypes}
        value={selectedType}
        onChange={(_event, documentType) => handleTypeChange(documentType)}
        getOptionLabel={(documentType) => documentType.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={typeInputRef}
            label={t('consultant.deadlines.create.typeLabel')}
            required
          />
        )}
      />

      {aiFilled.isStillAiProposed('documentTypeId', documentTypeId) && (
        <AiProposedLabel testId="ai-proposed-type" />
      )}

      <DocumentTypeHint documentType={selectedType} audience="consultant" />

      <ConsultantSameTypeNotice
        tenantIds={selectedTenantIds}
        documentType={selectedType}
        assigneeUserId={assigneeUserId}
        clientNames={clientNames}
        onNavigate={onClose}
      />

      {titleRequired && (
        <>
          <TextField
            {...register('title')}
            label={t('consultant.deadlines.create.titleLabel')}
            helperText={
              showTitleError
                ? t('consultant.deadlines.create.requiredError')
                : t('consultant.deadlines.create.titleHelper')
            }
            error={showTitleError}
            required
            fullWidth
            slotProps={{
              htmlInput: {
                maxLength: 120,
                style: { fontSize: 16 },
                'data-testid': 'consultant-deadline-create-title',
              },
            }}
          />
          <Controller
            name="domain"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label={t('consultant.deadlines.create.domainLabel')}
                value={field.value ?? ''}
                onChange={(event) => field.onChange(event.target.value || undefined)}
                fullWidth
              >
                <MenuItem value="" sx={{ minHeight: 48 }}>
                  {t('consultant.deadlines.create.domainNone')}
                </MenuItem>
                {DEADLINE_DOMAIN_VALUES.map((domain) => (
                  <MenuItem key={domain} value={domain} sx={{ minHeight: 48 }}>
                    {t(`consultant.deadlines.create.domain.${domain}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <HealthDataNotice />
        </>
      )}

      <DeadlineRecurrenceFields
        value={{ recurrence, dueAt, validityMonths }}
        onChange={handleRecurrenceChange}
        suggestion={suggestion}
        isSuggestionLoading={isSuggestionLoading}
        showRegionalHint={selectedType?.warningOnly === true}
        todayIso={todayIso}
        errors={{
          recurrence: Boolean(formState.errors.recurrence) && formState.isSubmitted,
          dueAt: Boolean(formState.errors.dueAt),
          validityMonths: Boolean(formState.errors.validityMonths),
        }}
        testIdPrefix="consultant-deadline-create-recurrence"
      />
      {aiFilled.areStillAiProposed(RECURRENCE_AI_FIELDS, {
        recurrence,
        dueAt,
        validityMonths,
      }) && <AiProposedLabel testId="ai-proposed-recurrence" />}

      <Controller
        name="assigneeUserId"
        control={control}
        render={({ field }) => (
          <ConsultantDeadlineAssigneeField
            tenantIds={selectedTenantIds}
            isPersonalType={selectedType?.isPersonal === true}
            hasDueDate={recurrence === 'ONE_OFF' || recurrence === 'RECURRING'}
            value={field.value ?? null}
            onChange={field.onChange}
          />
        )}
      />

      {asksWarningDays(recurrence) && (
        <Controller
          name="warningDays"
          control={control}
          render={({ field, fieldState }) => (
            <DeadlineWarningDaysField
              value={field.value ?? []}
              onChange={field.onChange}
              hasError={Boolean(fieldState.error)}
              idPrefix="consultant-deadline-create"
              dueAt={dueAt}
              translationPrefix="consultant.deadlines.create"
            />
          )}
        />
      )}

      <CreateMissingHint isSaveAllowed={isSaveAllowed} inputs={createInputs} />

      <AssistantCardBusyNotice visible={isAssistantCardBusy} />
    </FormDialog>
  );
}
